import { useVabsCurrenciesHook } from '@/entities/currency-account/model/hooks';

import {
  DashboardSelectDialog,
  DashboardSelectDialogTrigger,
  EmptyState,
  Skeleton,
} from '@/shared/ui';

import { ICurrency } from '../types';
import { CurrencyIcon } from './currency-icon';
import { CurrencyItem } from './currency-item';

interface CurrencySelectProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  searchValue: string;
  setSearchValue: (searchValue: string) => void;
  selectedCurrency: ICurrency | null;
  iconColor?: string;
  setSelectedCurrency: (currency: ICurrency) => void;
  excludedTickers?: string[];
}

/** VABS */
export const CurrencySelect = ({
  isOpen,
  setIsOpen,
  searchValue,
  setSearchValue,
  iconColor,
  selectedCurrency,
  setSelectedCurrency,
  excludedTickers = [],
}: CurrencySelectProps) => {
  const { data, isLoading } = useVabsCurrenciesHook();
  const currencies =
    data?.map((i) => ({
      id: i.id,
      ticker: i.ticker,
      name: i?.item.name || i.ticker,
      precision: 2,
      code: i.ticker,
      symbol: i.symbol || i.ticker,
    })) || [];
  const displayedCurrencies = currencies.filter((currency) => {
    const hasName = currency.name
      .toLowerCase()
      .includes(searchValue.toLowerCase());
    const hasTicker = currency.ticker
      .toLowerCase()
      .includes(searchValue.toLowerCase());
    return !excludedTickers.includes(currency.ticker) && (hasName || hasTicker);
  });

  return (
    <DashboardSelectDialog
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      placeholder="Search currency"
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      triggerProps={{ className: 'w-full' }}
      customTrigger={
        <DashboardSelectDialogTrigger
          iconColor={
            selectedCurrency
              ? 'var(--color-icon-base-500)'
              : 'var(--color-icon-base-300)'
          }
          variant="send"
          placeholder={selectedCurrency ? '' : 'Choose currency...'}
          value={selectedCurrency?.name ?? ''}
          inputClassName="pl-1 py-0"
          beforeIcon={
            selectedCurrency && (
              <CurrencyIcon ticker={selectedCurrency.ticker} />
            )
          }
        />
      }
    >
      <ul className="flex h-fit min-h-[72px] w-full flex-col pb-2">
        {isLoading && !displayedCurrencies.length ? (
          Array.from({ length: 5 }).map((_, index) => (
            <li key={index} className="my-1 px-1">
              <Skeleton className="h-[72px] w-full" />
            </li>
          ))
        ) : displayedCurrencies.length ? (
          displayedCurrencies.map((currency) => {
            return (
              <li
                key={currency.id}
                className="w-full"
                onClick={() => {
                  setSelectedCurrency(currency);
                  setIsOpen(false);
                }}
              >
                <CurrencyItem currency={currency} />
              </li>
            );
          })
        ) : (
          <EmptyState />
        )}
      </ul>
    </DashboardSelectDialog>
  );
};
